import { Component, HostListener, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Actions, Select, Store } from '@ngxs/store';
import { CertificationProvidersDataSource } from 'app/library/certification-provider/components/datasource';
import { CertificationProvider } from 'app/library/certification-provider/models/certification-provider';
import { Certification } from 'app/library/certifications/models/certification';
import { CertificationsState } from 'app/library/certifications/models/certifications.state';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { CertificationsDataSource } from '../../datasource';

interface Tag {
    text: string;
    id: string;
    type: 'provider' | 'certification';
    parentId?: string;
}

@Component({
    selector: 'app-certifications-filter-dialog',
    templateUrl: './certifications-filter-dialog.component.html',
    styleUrls: ['./certifications-filter-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class CertificationsFilterDialogComponent extends UnsubscribeOnDestroy implements OnInit {
    @Input()
    entitie$: Observable<CertificationProvider[]>;

    @Select(CertificationsState.getFilteredEntities)
    certifications$: Observable<Certification[]>;

    dataSource: CertificationProvidersDataSource;
    certificationsDataSource: CertificationsDataSource;
    displayedColumns = ['checkbox', 'name', 'buttons'];
    certificationsDisplayedColumns = ['name', 'checkbox'];
    removable = true;
    searchInput = new FormControl();
    list: Certification[] = [];
    checkboxes: { [key: string]: boolean } = {};
    certificationCheckboxes: { [key: string]: boolean } = {};
    isActionBoxHovered: boolean = false;
    selectedProvider: CertificationProvider;
    certifications: Certification[] = [];
    isCertificationsLoading: boolean = false;
    isCertificationProvidersLoading: boolean = false;

    selectedTags: Tag[] = [];

    selectedProviders: Set<Tag> = sessionStorage.getItem('certificationProvidersSelectedFilter')
        ? new Set(JSON.parse(sessionStorage.getItem('certificationProvidersSelectedFilter')))
        : new Set();

    selectedCertifications: Set<Tag> = sessionStorage.getItem('certificationsSelectedFilter')
        ? new Set(JSON.parse(sessionStorage.getItem('certificationsSelectedFilter')))
        : new Set();

    @HostListener('mouseenter')
    onMouseEnter(e: Event) {
        if (e) {
            this.isActionBoxHovered = true;
        }
    }

    @HostListener('mouseleave')
    onMouseLeave(e: Event) {
        if (e) {
            this.isActionBoxHovered = false;
        }
    }

    constructor(private _store: Store, public dialogRef: MatDialogRef<CertificationsFilterDialogComponent>, private _action$: Actions) {
        super();
    }

    async ngOnInit(): Promise<void> {
        this.selectedTags.push(...Array.from(this.selectedCertifications), ...Array.from(this.selectedProviders));

        this.searchInput.valueChanges.pipe(takeUntil(this._unsubscribeAll), debounceTime(1000), distinctUntilChanged()).subscribe((searchText) => {
            if (this.selectedProvider) {
                this.loadCertifications(this.selectedProvider);
            } else {
                this.loadCertificationProviders();
            }
        });

        this.dataSource = new CertificationProvidersDataSource(this.entitie$, this._store, this._action$);
        this.certificationsDataSource = new CertificationsDataSource(this.certifications$, this._store, this._action$);

        setTimeout(() => {
            this.loadCertificationProviders();
        });

        this.entitie$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((providers) => !!providers)
            )
            .subscribe((providers) => {
                providers.forEach((provider) => {
                    // Set the checkbox based on whether the provider's id is in selectedProviderIds
                    if (Array.from(this.selectedProviders).some((tag) => tag.id === provider.id)) {
                        for (const item of this.selectedProviders) {
                            if (item.id === provider.id) {
                                this.checkboxes[provider.id] = true;
                            }
                        }
                    }

                    // If the provider's id is in selectedProviderIds, add it to selectedTags
                    if (Array.from(this.selectedProviders).some((tag) => tag.id === provider.id)) {
                        if (this.selectedTags.find((tag) => tag.id === provider.id && tag.type === 'provider')) {
                            return;
                        }

                        this.selectedTags.push({
                            text: `${provider.providerName}: All`,
                            id: provider.id,
                            type: 'provider',
                        });
                    }
                });
            });

        this.certifications$
            .pipe(
                filter((certifications) => !!certifications),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((certifications) => {
                this.certifications = certifications;

                certifications.forEach((certification) => {
                    // Set the checkbox based on whether the certification's id is in selectedCertificationIds
                    if (Array.from(this.selectedCertifications).some((tag) => tag.id === certification.id)) {
                        for (const item of this.selectedCertifications) {
                            if (item.id === certification.id) {
                                this.certificationCheckboxes[certification.id] = true;
                            }
                        }

                        if (this.selectedTags.find((tag) => tag.id === certification.id && tag.type === 'certification')) {
                            return;
                        }
                    }

                    if (Array.from(this.selectedProviders).some((tag) => tag.id === this.selectedProvider?.id)) {
                        for (const item of this.selectedProviders) {
                            if (item.id === this.selectedProvider.id) {
                                this.certificationCheckboxes[certification.id] = true;

                                if (!this.selectedCertifications.size || !Array.from(this.selectedCertifications).some((tag) => tag.id === certification.id)) {
                                    this.selectedCertifications.add({
                                        id: certification.id,
                                        text: `${certification.provider.providerName}: ${certification.certificationName}`,
                                        type: 'certification',
                                        parentId: certification.provider.id,
                                    });
                                }
                            }
                        }
                    }
                });
            });
    }

    toggleProviderSelection(provider: CertificationProvider, isCertificationListVisible: boolean = false): void {
        if (Array.from(this.selectedProviders).some((tag) => tag.id === provider.id)) {
            for (const item of this.selectedProviders) {
                if (item.id === provider.id) {
                    this.selectedProviders.delete(item);
                    this.checkboxes[provider.id] = false;
                    break;
                }
            }

            this.selectedCertifications.forEach((tag) => {
                if (tag.parentId === provider.id) {
                    this.selectedCertifications.delete(tag);
                    this.removeTag(tag.id, 'certification');
                    this.certificationCheckboxes[tag.id] = false;
                }
            });

            this.removeTag(provider.id, 'provider');
        } else {
            this.selectedCertifications.forEach((tag) => {
                if (tag.parentId === provider.id) {
                    this.selectedCertifications.delete(tag);
                    this.removeTag(tag.id, 'certification');
                }
            });

            if (isCertificationListVisible) {
                this.certifications.forEach((certification) => {
                    this.certificationCheckboxes[certification.id] = true;
                    this.selectedCertifications.add({
                        id: certification.id,
                        text: `${certification.provider.providerName}: ${certification.certificationName}`,
                        type: 'certification',
                        parentId: provider.id,
                    });
                });
            }

            this.selectedProviders.add({ id: provider.id, text: `${provider.providerName}: All`, type: 'provider' });
            this.addTag(`${provider.providerName}: All`, provider.id, 'provider');
        }
    }

    toggleCertificationSelection(certification: Certification): void {
        if (Array.from(this.selectedCertifications).some((tag) => tag.id === certification.id)) {
            for (const item of this.selectedCertifications) {
                if (item.id === certification.id) {
                    this.selectedCertifications.delete(item);
                    this.certificationCheckboxes[certification.id] = false;
                    break;
                }
            }

            if (Array.from(this.selectedProviders).some((tag) => tag.id === certification.provider.id)) {
                for (const item of this.selectedProviders) {
                    if (item.id === certification.provider.id) {
                        this.selectedProviders.delete(item);
                        this.checkboxes[certification.provider.id] = false;
                        this.certifications
                            .filter((cert) => cert.id !== certification.id)
                            .forEach((certification) => {
                                this.addTag(
                                    `${certification.provider.providerName}: ${certification.certificationName}`,
                                    certification.id,
                                    'certification',
                                    certification.provider.id
                                );
                            });
                        break;
                    }
                }

                this.removeTag(certification.provider.id, 'provider', false);
            }

            this.removeTag(certification.id, 'certification');
        } else {
            this.selectedCertifications.add({
                id: certification.id,
                text: `${certification.provider.providerName}: ${certification.certificationName}`,
                type: 'certification',
                parentId: certification.provider.id,
            });
            this.certificationCheckboxes[certification.id] = true;
            this.addTag(`${certification.provider.providerName}: ${certification.certificationName}`, certification.id, 'certification', certification.provider.id);
        }
    }

    addTag(text: string, id: string, type: 'provider' | 'certification', parentId?: string): void {
        this.selectedTags.push({ text, id, type, parentId });
    }

    removeTag(id: string, type: string, shouldDeselectCertifications: boolean = true): void {
        this.selectedTags = this.selectedTags.filter((tag) => tag.id !== id);
        if (type === 'provider') {
            for (const item of this.selectedProviders) {
                if (item.id === id) {
                    this.selectedProviders.delete(item);
                    break;
                }
            }

            if (shouldDeselectCertifications) {
                this.selectedCertifications.forEach((tag) => {
                    if (tag.parentId === id) {
                        this.selectedCertifications.delete(tag);
                        this.certificationCheckboxes[tag.id] = false;
                    }
                });
            }
        } else {
            for (const item of this.selectedCertifications) {
                if (item.id === id) {
                    this.selectedCertifications.delete(item);
                    break;
                }
            }
        }
    }

    onTagRemove(tag: Tag): void {
        if (Array.from(this.selectedProviders).some((tag) => tag.id === tag.id)) {
            for (const item of this.selectedProviders) {
                if (item.id === tag.id) {
                    this.selectedProviders.delete(item);
                    this.checkboxes[item.id] = false;
                    break;
                }
            }
        } else if (Array.from(this.selectedCertifications).some((tag) => tag.id === tag.id)) {
            for (const item of this.selectedCertifications) {
                if (item.id === tag.id) {
                    this.selectedCertifications.delete(item);
                    this.certificationCheckboxes[item.id] = false;
                    break;
                }
            }
        }
        this.removeTag(tag.id, tag.type);
    }

    clearSelection(): void {
        this.selectedProviders.clear();
        this.selectedCertifications.clear();
        this.checkboxes = {};
        this.certificationCheckboxes = {};
        this.selectedTags = [];
    }

    async showCertifications(provider: CertificationProvider): Promise<void> {
        this.searchInput.setValue('');
        this.selectedProvider = provider;
        this.isCertificationsLoading = true;
        await this.loadCertifications(this.selectedProvider);
        setTimeout(() => {
            this.isCertificationsLoading = false;
        }, 1000);
    }

    async goBackToProviders(): Promise<void> {
        this.searchInput.setValue('');
        this.selectedProvider = null;
        this.isCertificationProvidersLoading = true;
        await this.loadCertificationProviders();
        setTimeout(() => {
            this.isCertificationProvidersLoading = false;
        }, 1000);
    }

    saveSelected(): void {
        this.dialogRef.close({ selectedCertifications: this.selectedCertifications, selectedProviders: this.selectedProviders });
    }

    async loadCertificationProviders(): Promise<void> {
        if (this.dataSource) {
            this.dataSource.loadCertificationProviders(100, 1, this.searchInput.value, [], []);
        }
    }

    async loadCertifications(provider: CertificationProvider): Promise<void> {
        if (this.dataSource) {
            this.certificationsDataSource.loadCertifications(100, 1, this.searchInput.value, [provider.id], []);
        }
    }
}
